// Shared controller for the three Publish-profiles variants. Owns
// the react-hook-form instance, the TUS upload, the bundle list +
// preview queries, the detection-driven side effects, and the
// release mutation. Each variant is purely presentational and
// consumes this controller; behavior must stay identical across
// variants so we can swap freely.

import { zodResolver } from "@hookform/resolvers/zod";
import { type PublishProfilesInput, PublishProfilesSchema } from "@schemas";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { type UseTusUploadResult, useTusUpload } from "@veil/ui/upload";
import { useCallback, useEffect, useMemo } from "react";
import { type UseFormReturn, useForm } from "react-hook-form";
import { toast } from "sonner";
import { useLocation } from "wouter";
import type { PreviewData } from "@/components/profile-preview-card";
import { api, unwrap } from "@/lib/api";

export interface RawBundle {
	id: string;
	filename: string;
	sha256: string;
	sizeBytes: number;
	uploadedAt: string;
	detectedFormat?: string | null;
	fileCount?: number | null;
}

interface BundlesResponse {
	success: boolean;
	bundles: RawBundle[];
}

interface PreviewResponse {
	success: boolean;
	preview: PreviewData;
}

export interface PublishProfilesController {
	form: UseFormReturn<PublishProfilesInput>;
	upload: UseTusUploadResult<RawBundle>;
	bundles: RawBundle[];
	bundlesLoading: boolean;
	preview: PreviewData | null;
	previewLoading: boolean;
	previewError: string | null;
	detectedMajors: number[];
	bundleId: string;
	browserMajor: number;
	bumpType: PublishProfilesInput["bumpType"];
	majorMismatch: boolean;
	formatBlocked: boolean;
	submitting: boolean;
	onSubmit: (e?: React.BaseSyntheticEvent) => void;
	selectBundle: (id: string) => void;
	setBrowserMajor: (m: number) => void;
	setBumpType: (b: PublishProfilesInput["bumpType"]) => void;
}

export function usePublishProfilesController(): PublishProfilesController {
	const [, navigate] = useLocation();
	const queryClient = useQueryClient();

	const form = useForm<PublishProfilesInput>({
		resolver: zodResolver(PublishProfilesSchema),
		defaultValues: { rawBundleId: "", browserMajor: 147, bumpType: "patch" },
		mode: "onSubmit",
	});

	const bundlesQuery = useQuery({
		queryKey: ["bundles"],
		queryFn: () =>
			unwrap(api.api.upload["raw-profiles"].get()) as Promise<BundlesResponse>,
	});

	const resolveProfileBundle = useCallback(
		async (bundleId: string | null): Promise<RawBundle | null> => {
			const res = await fetch(`/api/upload/raw-profiles`, {
				credentials: "same-origin",
			});
			const json = (await res.json()) as BundlesResponse;
			if (bundleId) {
				return json.bundles.find((b) => b.id === bundleId) ?? null;
			}
			return json.bundles[0] ?? null;
		},
		[],
	);

	const upload = useTusUpload<RawBundle>({
		endpoint: "/api/upload/tus",
		successHeader: "X-Bundle-Id",
		resolveCompleted: resolveProfileBundle,
		onComplete: (bundle) => {
			toast.success(`Uploaded ${bundle.filename}`);
			form.setValue("rawBundleId", bundle.id, { shouldValidate: true });
			queryClient.invalidateQueries({ queryKey: ["bundles"] });
		},
	});

	const submitMutation = useMutation({
		mutationFn: async (values: PublishProfilesInput) => {
			return unwrap(api.api.releases.profiles.post(values)) as Promise<{
				operationId: string;
			}>;
		},
		onSuccess: ({ operationId }) => {
			toast.success("Release started");
			navigate(`/operations/${operationId}`);
		},
		onError: (err) =>
			toast.error(err instanceof Error ? err.message : "Release failed"),
	});

	const onSubmit = form.handleSubmit((values) => submitMutation.mutate(values));
	const bundleId = form.watch("rawBundleId");
	const bumpType = form.watch("bumpType");
	const browserMajor = form.watch("browserMajor");

	const previewQuery = useQuery({
		queryKey: ["bundle-preview", bundleId],
		enabled: !!bundleId,
		queryFn: async () => {
			const res = await fetch(
				`/api/upload/raw-profiles/${encodeURIComponent(bundleId)}/preview`,
				{ credentials: "same-origin" },
			);
			if (!res.ok) {
				throw new Error(`Preview request failed (${res.status})`);
			}
			return (await res.json()) as PreviewResponse;
		},
		staleTime: 60_000,
	});

	const preview = previewQuery.data?.preview ?? null;
	const detectedMajors = useMemo<number[]>(() => {
		const list = preview?.detectedMajors ?? [];
		return list.map((m) => m.major);
	}, [preview]);

	// Same auto-pin / clear-default behavior as the original page: pin to
	// the only detected major when there's exactly one, clear the default
	// 147 when it isn't present in the detected set so the operator has
	// to pick explicitly.
	useEffect(() => {
		if (!preview) return;
		if (detectedMajors.length === 1 && detectedMajors[0] !== undefined) {
			form.setValue("browserMajor", detectedMajors[0], {
				shouldValidate: true,
			});
		} else if (
			detectedMajors.length > 1 &&
			!detectedMajors.includes(browserMajor)
		) {
			form.setValue("browserMajor", detectedMajors[0] ?? 0, {
				shouldValidate: true,
			});
		}
	}, [preview, detectedMajors, browserMajor, form]);

	const majorMismatch =
		!!preview &&
		detectedMajors.length > 0 &&
		!detectedMajors.includes(browserMajor);
	const formatBlocked =
		!!preview &&
		(preview.detectedFormat === "unknown" || detectedMajors.length === 0);

	return {
		form,
		upload,
		bundles: bundlesQuery.data?.bundles ?? [],
		bundlesLoading: bundlesQuery.isPending,
		preview,
		previewLoading: previewQuery.isPending,
		previewError:
			previewQuery.error instanceof Error ? previewQuery.error.message : null,
		detectedMajors,
		bundleId,
		browserMajor,
		bumpType,
		majorMismatch,
		formatBlocked,
		submitting: submitMutation.isPending,
		onSubmit,
		selectBundle: (id: string) =>
			form.setValue("rawBundleId", id, { shouldValidate: true }),
		setBrowserMajor: (m: number) =>
			form.setValue("browserMajor", m, { shouldValidate: true }),
		setBumpType: (b) => form.setValue("bumpType", b, { shouldDirty: true }),
	};
}
